import { common } from "@shared/constants";
import { createContext, useContext, useMemo } from "react";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useCustomQuestions } from "@shared/modules/checkout/hooks/useCustomQuestions";
import { useEnhancedInfo } from "@shared/modules/checkout/hooks/useEnhancedInfo";

type CustomQuestionsType = {
  isCustomQuestionsNeeded: boolean;
  checkoutForms: ReturnType<typeof useCheckoutForms>;
  areFormsValid: () => Promise<boolean>;
};

const CustomQuestions = createContext<CustomQuestionsType | null>(null);

export const CustomQuestionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const checkoutForms = useCheckoutForms();
  const { itemGroups, isGroupSale } = usePublicBasket();
  const { hasEnhancedInfo, hasAddressInfo } = useEnhancedInfo(
    common.DataProvider.Public,
    true
  );

  const { hasCustomQuestions } = useCustomQuestions(
    common.DataProvider.Public,
    true
  );

  const hasMultiGuestItemGroups =
    itemGroups.filter((ig) => ig.info_required).length > 0 && !isGroupSale;

  const isCustomQuestionsNeeded = useMemo(() => {
    return (
      hasEnhancedInfo() ||
      hasAddressInfo() ||
      hasCustomQuestions() ||
      hasMultiGuestItemGroups
    );
  }, [hasEnhancedInfo, hasAddressInfo, hasCustomQuestions]);

  const { areFormsValid } = checkoutForms;

  const value = {
    isCustomQuestionsNeeded,
    checkoutForms,
    areFormsValid,
  };

  return (
    <CustomQuestions.Provider value={value}>
      {children}
    </CustomQuestions.Provider>
  );
};

export const useCustomQuestionsContext = () => {
  const context = useContext(CustomQuestions);

  if (!context) {
    throw new Error(
      "useCustomQuestions must be used within a CustomQuestionsProvider."
    );
  }
  return context;
};
